import { dia } from "@clientio/rappid";
import { State } from "../mvc/state";
import { Transition } from "../mvc/transition";
import { DirtyState } from "./dirty-state";
import { RappidCoreContext } from "../rappid/rappid-core-context";
import { Final } from "../mvc/final";
import { Choice } from "../mvc/choice";

class StateHighlighter extends dia.HighlighterView {
  MOUNTABLE = false;

  highlight(view: dia.CellView) {
    DirtyState.listenForChange = false;
    const body = this.findNode(view, "body");
    body.classList.add("state-fade");
    body.classList.add("active-state");
    DirtyState.listenForChange = true;
  }

  unhighlight(view: dia.CellView) {
    DirtyState.listenForChange = false;
    const body = this.findNode(view, "body");
    body.classList.remove("active-state");
    body.classList.remove("state-fade");
    DirtyState.listenForChange = true;
  }
}

class TransitionHighlighter extends dia.HighlighterView {
  MOUNTABLE = false;

  highlight(view: dia.CellView) {
    DirtyState.listenForChange = false;
    const line = this.findNode(view, "line");
    line.classList.add("transition-fade");
    line.classList.add("link-animation");
    line.classList.add("active-transition");
    DirtyState.listenForChange = true;
  }

  unhighlight(view: dia.CellView) {
    DirtyState.listenForChange = false;
    const line = this.findNode(view, "line");
    line.classList.remove("link-animation");
    line.classList.remove("transition-fade");
    line.classList.remove("active-transition");
    DirtyState.listenForChange = true;
  }
}

class FinalHighlighter extends dia.HighlighterView {
  MOUNTABLE = false;

  highlight(view: dia.CellView) {
    DirtyState.listenForChange = false;
    const body = this.findNode(view, "innerBlack");
    body.classList.add("final-fade");
    body.classList.add("active-final");
    DirtyState.listenForChange = true;
  }

  unhighlight(view: dia.CellView) {
    DirtyState.listenForChange = false;
    const body = this.findNode(view, "innerBlack");
    body.classList.remove("active-final");
    body.classList.remove("final-fade");
    DirtyState.listenForChange = true;
  }
}

export class Highlighter {
  private static INSTANCE: Highlighter;

  static getInstance() {
    if (!Highlighter.INSTANCE) {
      Highlighter.INSTANCE = new Highlighter();
    }
    return Highlighter.INSTANCE;
  }

  private contexts: RappidCoreContext[] = [];

  addContext(context: RappidCoreContext) {
    this.contexts.push(context);
  }

  removeContext(context: RappidCoreContext) {
    this.contexts.indexOf(context) > -1 && this.contexts.splice(this.contexts.indexOf(context), 1);
  }

  highlightedStates: Set<State> = new Set();
  highlightedFinals: Set<Final> = new Set();
  highlightedTransitions: Set<Transition> = new Set();
  private cycleInProgress = false;
  private pendingChoices: Set<Choice> = new Set();

  stateEntered(stateId: string) {
    const state = this.getState(stateId);
    this.resolvePendingChoice(stateId);
    this.highlightState(state);

    const final = this.getFinal(stateId);
    this.highlightFinal(final);
  }

  stateExited(stateId: string) {
    const state = this.getState(stateId);
    this.unhighlightState(state);

    const final = this.getFinal(stateId);
    this.unhighlightFinal(final);
  }

  reactionFired(stateId: string, transId: string) {
    if (!this.cycleInProgress) {
      this.cycleInProgress = true;
      setTimeout(() => (this.cycleInProgress = false));
      this.pendingChoices.clear();
      this.highlightedTransitions.forEach((t) => {
        this.unhighlightTransition(t);
      });
    }
    const transition: Transition = this.getTransition(stateId, transId);
    this.highlightTransition(transition);

    const target = transition?.getTargetCell();
    if (target instanceof Choice) this.pendingChoices.add(target);
  }

  private resolvePendingChoice(enteredId: string) {
    for (const choice of this.pendingChoices) {
      const paths = this.findChoicePaths(choice, enteredId, new Set());
      if (paths.length === 0) continue;

      this.pendingChoices.delete(choice);
      paths.flat().forEach((t) => this.highlightTransition(t));
      return;
    }
  }

  private findChoicePaths(choice: Choice, targetId: string, visited: Set<Choice>): Transition[][] {
    if (visited.has(choice)) return [];
    visited.add(choice);

    const paths: Transition[][] = [];
    choice.getOutgoingTransitions().forEach((t) => {
      const target = t.getTargetCell();
      if (target?.id === targetId) {
        paths.push([t]);
      } else if (target instanceof Choice) {
        this.findChoicePaths(target, targetId, visited).forEach((p) => paths.push([t, ...p]));
      }
    });
    return paths;
  }

  clearAll() {
    this.pendingChoices.clear();
    this.highlightedStates.forEach((s) => {
      this.unhighlightState(s);
    });
    this.highlightedFinals.forEach((f) => {
      this.unhighlightFinal(f);
    });
    this.highlightedTransitions.forEach((t) => {
      this.unhighlightTransition(t);
    });
  }

  showAll() {
    this.highlightedStates.forEach((s) => {
      this.contexts.forEach((context) => {
        this.setStateHighlighterClass(context.paper, s);
      });
    });
    this.highlightedTransitions.forEach((t) => {
      this.contexts.forEach((context) => {
        this.setTransitionHighlighterClass(context.paper, t);
      });
    });
    this.highlightedFinals.forEach((f) => {
      this.contexts.forEach((context) => {
        this.setFinalHighlighterClass(context.paper, f);
      });
    });
  }

  highlightTransition(t: Transition) {
    if (!t) return;

    this.highlightedTransitions.add(t);

    this.contexts.forEach((context) => {
      this.setTransitionHighlighterClass(context.paper, t);
    });
  }

  unhighlightTransition(t: Transition) {
    if (!t) return;

    this.highlightedTransitions.delete(t);

    this.contexts.forEach((context) => {
      this.removeTransitionHighlighterClass(context.paper, t);
    });
  }

  highlightState(s: State) {
    if (!s) return;

    this.highlightedStates.add(s);

    this.contexts.forEach((context) => {
      this.setStateHighlighterClass(context.paper, s);
    });
  }

  unhighlightState(s: State) {
    if (!s) return;

    this.highlightedStates.delete(s);

    this.contexts.forEach((context) => {
      this.removeStateHighlighterClass(context.paper, s);
    });
  }

  highlightFinal(f: Final) {
    if (!f) return;

    this.highlightedFinals.add(f);

    this.contexts.forEach((context) => {
      this.setFinalHighlighterClass(context.paper, f);
    });
  }

  unhighlightFinal(f: Final) {
    if (!f) return;

    this.highlightedFinals.delete(f);

    this.contexts.forEach((context) => {
      this.removeFinalHighlighterClass(context.paper, f);
    });
  }

  getState(stateId: string): State {
    return this.getStates().find((c) => c.id === stateId) as State;
  }

  getFinal(stateId: string): Final {
    return this.getFinals().find((c) => c.id === stateId) as Final;
  }

  getStates(): Array<State> {
    /* eslint-disable @typescript-eslint/no-explicit-any*/
    return this.contexts[0].paper.model.getElements().filter((c: any) => c instanceof State) as State[];
    /* eslint-enable @typescript-eslint/no-explicit-any*/
  }

  getFinals(): Array<Final> {
    /* eslint-disable @typescript-eslint/no-explicit-any*/
    return this.contexts[0].paper.model.getElements().filter((c: any) => c instanceof Final) as Final[];
    /* eslint-enable @typescript-eslint/no-explicit-any*/
  }

  getTransition(stateId: string, transId: string) {
    return this.getState(stateId).getOutgoingTransitions()[transId];
  }

  private setStateHighlighterClass(paper: dia.Paper, state: State) {
    if (!paper) return;
    const view = state.findView(paper) as dia.ElementView;
    if (!view) return;
    StateHighlighter.add(view, "root", "state-highlighter");
  }

  private removeStateHighlighterClass(paper: dia.Paper, state: State) {
    if (!paper) return;
    const view = state.findView(paper) as dia.ElementView;
    if (!view) return;
    StateHighlighter.remove(view, "state-highlighter");
  }

  private setTransitionHighlighterClass(paper: dia.Paper, transition: Transition) {
    if (!paper) return;
    const view = transition.findView(paper) as dia.ElementView;
    if (!view) return;
    TransitionHighlighter.add(view, "root", "transition-highlighter");
  }

  private removeTransitionHighlighterClass(paper: dia.Paper, transition: Transition) {
    if (!paper) return;
    const view = transition.findView(paper) as dia.ElementView;
    if (!view) return;
    TransitionHighlighter.remove(view, "transition-highlighter");
  }

  private setFinalHighlighterClass(paper: dia.Paper, final: Final) {
    if (!paper) return;
    const view = final.findView(paper) as dia.ElementView;
    if (!view) return;
    FinalHighlighter.add(view, "root", "final-highlighter");
  }

  private removeFinalHighlighterClass(paper: dia.Paper, final: Final) {
    if (!paper) return;
    const view = final.findView(paper) as dia.ElementView;
    if (!view) return;
    FinalHighlighter.remove(view, "final-highlighter");
  }
}
